// Billing & Settlement mock data — gestione costi e conguagli farmacia ↔ NewCo
// Categorie prezzo derivate dalla tipologia esame:
//  - instrumental: esame strumentale puro (quota Struttura = 0)
//  - instrumental_reported: esame con referto (quota Struttura attiva)
//  - visit: visita (quota Struttura attiva)

import { serviceExtendedData, getExamTypeById } from "./adminConfigData";
import { servicesData, patientsData } from "./mockData";
import { pharmaciesData } from "./platformData";

export const priceCategories = [
  { id: "instrumental", label: "Esame strumentale", short: "Strumentale",
    color: "bg-sky-50 text-sky-700 border-sky-200" },
  { id: "instrumental_reported", label: "Esame strumentale con refertazione", short: "Con referto",
    color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { id: "visit", label: "Visita", short: "Visita",
    color: "bg-violet-50 text-violet-700 border-violet-200" }
];

export const priceCategoryLabels = priceCategories.reduce(
  (acc, c) => ({ ...acc, [c.id]: c.label }), {}
);

export const getPriceCategoryFromType = (typeId) => {
  const type = getExamTypeById(typeId);
  if (!type) return "instrumental";
  if (type.isVisit) return "visit";
  if (type.requiresReport) return "instrumental_reported";
  return "instrumental";
};

export const getPriceCategoryConfig = (id) =>
  priceCategories.find(c => c.id === id) || priceCategories[0];

// Quote-snapshot helper: prende il prezzo definito sul servizio + la categoria
export const getServicePricing = (serviceId) => {
  const ext = serviceExtendedData.find(s => s.serviceId === serviceId);
  const svc = servicesData.find(s => s.id === serviceId);
  if (!ext || !svc) return null;
  const category = getPriceCategoryFromType(ext.typeId);
  return {
    serviceId,
    serviceName: svc.name,
    typeId: ext.typeId,
    category,
    prezzoPubblico: ext.prezzoPubblico,
    quotaNewCo: ext.costoGestione,
    quotaFarmacia: ext.costoFarmacia,
    quotaStruttura: category === "instrumental" ? 0 : ext.costoMedico
  };
};

// ─────────────────────────────────────────────────────────────────────
// Mock payment transactions — 18 mesi di transazioni distribuite su farmacie
// Ogni transazione storicizza le quote applicate (snapshot) per audit.
// ─────────────────────────────────────────────────────────────────────

// Seed deterministico per generare ~60 transazioni mock
const txSeeds = [
  // farmaciaId, serviceId, paymentMethod, dateISO, patientId
  ["ph1", "ecg",                 "electronic", "2025-09-12", "p1"],
  ["ph1", "spirometria",         "cash",       "2025-09-18", "p2"],
  ["ph1", "pressione",           "cash",       "2025-09-20", "p3"],
  ["ph1", "glicemia",            "cash",       "2025-09-22", "p4"],
  ["ph1", "ecg",                 "electronic", "2025-10-05", "p1"],
  ["ph1", "emocromo",            "electronic", "2025-10-10", "p5"],
  ["ph1", "colesterolo",         "cash",       "2025-10-15", "p6"],
  ["ph1", "pressione",           "cash",       "2025-10-18", "p2"],
  ["ph1", "spirometria",         "electronic", "2025-10-22", "p3"],
  ["ph1", "ecg",                 "cash",       "2025-11-02", "p4"],
  ["ph1", "emoglobina_glicata",  "electronic", "2025-11-08", "p1"],
  ["ph1", "glicemia",            "cash",       "2025-11-12", "p5"],
  ["ph1", "trigliceridi",        "cash",       "2025-11-15", "p6"],
  ["ph1", "ecg",                 "electronic", "2025-11-20", "p2"],
  ["ph1", "spirometria",         "cash",       "2025-12-03", "p3"],
  ["ph1", "ossimetria",          "cash",       "2025-12-08", "p4"],
  ["ph1", "ecg",                 "electronic", "2025-12-15", "p1"],
  ["ph1", "colesterolo",         "electronic", "2025-12-20", "p5"],
  ["ph1", "spirometria",         "cash",       "2026-01-10", "p2"],
  ["ph1", "ecg",                 "electronic", "2026-01-15", "p3"],
  ["ph1", "emocromo",            "electronic", "2026-01-20", "p6"],
  ["ph1", "glicemia",            "cash",       "2026-01-25", "p4"],
  ["ph1", "ecg",                 "electronic", "2026-02-02", "p1"],
  ["ph1", "pressione",           "cash",       "2026-02-08", "p5"],
  ["ph1", "spirometria",         "cash",       "2026-02-14", "p2"],
  ["ph1", "emoglobina_glicata",  "electronic", "2026-02-18", "p3"],
  ["ph1", "trigliceridi",        "cash",       "2026-02-22", "p6"],
  // ph2
  ["ph2", "ecg",                 "cash",       "2025-10-08", "p1"],
  ["ph2", "spirometria",         "electronic", "2025-10-20", "p2"],
  ["ph2", "pressione",           "cash",       "2025-11-05", "p3"],
  ["ph2", "ecg",                 "electronic", "2025-11-18", "p4"],
  ["ph2", "glicemia",            "cash",       "2025-12-02", "p5"],
  ["ph2", "emocromo",            "electronic", "2025-12-15", "p6"],
  ["ph2", "ecg",                 "cash",       "2026-01-12", "p1"],
  ["ph2", "colesterolo",         "cash",       "2026-01-22", "p2"],
  ["ph2", "spirometria",         "electronic", "2026-02-05", "p3"],
  ["ph2", "ecg",                 "electronic", "2026-02-15", "p4"],
  ["ph2", "pressione",           "cash",       "2026-02-20", "p5"],
  // ph3
  ["ph3", "ecg",                 "electronic", "2025-11-10", "p1"],
  ["ph3", "spirometria",         "cash",       "2025-11-22", "p2"],
  ["ph3", "ecg",                 "electronic", "2025-12-05", "p3"],
  ["ph3", "emoglobina_glicata",  "electronic", "2025-12-18", "p4"],
  ["ph3", "glicemia",            "cash",       "2026-01-08", "p5"],
  ["ph3", "pressione",           "cash",       "2026-01-18", "p6"],
  ["ph3", "ecg",                 "electronic", "2026-02-03", "p1"],
  ["ph3", "spirometria",         "cash",       "2026-02-12", "p2"],
  ["ph3", "colesterolo",         "electronic", "2026-02-19", "p3"]
];

export const paymentTransactionsData = txSeeds.map(([pharmacyId, serviceId, method, date, patientId], idx) => {
  const pricing = getServicePricing(serviceId);
  if (!pricing) return null;
  return {
    id: `tx${String(idx + 1).padStart(4, "0")}`,
    pharmacyId,
    serviceId,
    serviceName: pricing.serviceName,
    category: pricing.category,
    patientId,
    date, // YYYY-MM-DD
    paymentMethod: method, // "cash" | "electronic"
    amount: pricing.prezzoPubblico, // snapshot
    quotaNewCo: pricing.quotaNewCo,
    quotaFarmacia: pricing.quotaFarmacia,
    quotaStruttura: pricing.quotaStruttura
  };
}).filter(Boolean);

// ─────────────────────────────────────────────────────────────────────
// Settlements (conguagli storicizzati)
// status: "open" | "settled"
// balance: cash incassato − quota farmacia dovuta (>0 = farmacia deve a NewCo)
// ─────────────────────────────────────────────────────────────────────

export const settlementStatuses = [
  { id: "open", label: "Aperto", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { id: "settled", label: "Saldato", color: "bg-emerald-50 text-emerald-700 border-emerald-200" }
];

export const getSettlementStatusConfig = (id) =>
  settlementStatuses.find(s => s.id === id) || settlementStatuses[0];

// Genera periodo mensile (YYYY-MM-01 → ultimo giorno)
export const monthBounds = (yyyymm) => {
  const [y, m] = yyyymm.split("-").map(Number);
  const start = `${yyyymm}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const end = `${yyyymm}-${String(lastDay).padStart(2, "0")}`;
  return { start, end };
};

// Filtra transazioni per pharmacy + range date
export const filterTransactions = (txs, { pharmacyId, from, to, category, paymentMethod }) => {
  return txs.filter(t => {
    if (pharmacyId && t.pharmacyId !== pharmacyId) return false;
    if (from && t.date < from) return false;
    if (to && t.date > to) return false;
    if (category && category !== "all" && t.category !== category) return false;
    if (paymentMethod && paymentMethod !== "all" && t.paymentMethod !== paymentMethod) return false;
    return true;
  });
};

// Calcola riepilogo economico da un set di transazioni
export const computeBillingSummary = (txs) => {
  const init = {
    totalValue: 0,
    totalElectronic: 0,
    totalCash: 0,
    totalQuotaNewCo: 0,
    totalQuotaFarmacia: 0,
    totalQuotaStruttura: 0,
    count: 0,
    byCategory: {
      instrumental: { count: 0, value: 0, newco: 0, farmacia: 0, struttura: 0 },
      instrumental_reported: { count: 0, value: 0, newco: 0, farmacia: 0, struttura: 0 },
      visit: { count: 0, value: 0, newco: 0, farmacia: 0, struttura: 0 }
    }
  };
  return txs.reduce((acc, t) => {
    acc.count += 1;
    acc.totalValue += t.amount;
    if (t.paymentMethod === "electronic") acc.totalElectronic += t.amount;
    else acc.totalCash += t.amount;
    acc.totalQuotaNewCo += t.quotaNewCo;
    acc.totalQuotaFarmacia += t.quotaFarmacia;
    acc.totalQuotaStruttura += t.quotaStruttura;
    const c = acc.byCategory[t.category];
    if (c) {
      c.count += 1;
      c.value += t.amount;
      c.newco += t.quotaNewCo;
      c.farmacia += t.quotaFarmacia;
      c.struttura += t.quotaStruttura;
    }
    return acc;
  }, init);
};

// Conguaglio: cash incassato in farmacia − quota farmacia dovuta totale
// >0 → farmacia ha incassato cash > sua quota → deve dare a NewCo la differenza
// <0 → farmacia ha incassato cash < sua quota → NewCo deve dare alla farmacia la differenza
export const computeBalance = (summary) =>
  +(summary.totalCash - summary.totalQuotaFarmacia).toFixed(2);

// Seed di conguagli storici (chiusure mensili già fatte)
// I valori sono pre-calcolati per riflettere coerentemente le transazioni del mese.
const buildSettlement = (id, pharmacyId, period, status, settledAt = null, settledBy = null) => {
  const { start, end } = monthBounds(period);
  const txs = filterTransactions(paymentTransactionsData, { pharmacyId, from: start, to: end });
  const summary = computeBillingSummary(txs);
  return {
    id,
    pharmacyId,
    period, // YYYY-MM
    periodStart: start,
    periodEnd: end,
    totalValue: summary.totalValue,
    totalElectronic: summary.totalElectronic,
    totalCash: summary.totalCash,
    quotaFarmaciaDue: summary.totalQuotaFarmacia,
    quotaNewCoDue: summary.totalQuotaNewCo,
    quotaStrutturaDue: summary.totalQuotaStruttura,
    balance: computeBalance(summary),
    transactionCount: summary.count,
    status,
    settledAt,
    settledBy,
    createdAt: end
  };
};

export const settlementsData = [
  // ph1
  buildSettlement("st-ph1-202509", "ph1", "2025-09", "settled", "2025-10-05", "admin@witapp.it"),
  buildSettlement("st-ph1-202510", "ph1", "2025-10", "settled", "2025-11-04", "admin@witapp.it"),
  buildSettlement("st-ph1-202511", "ph1", "2025-11", "settled", "2025-12-03", "admin@witapp.it"),
  buildSettlement("st-ph1-202512", "ph1", "2025-12", "settled", "2026-01-08", "admin@witapp.it"),
  buildSettlement("st-ph1-202601", "ph1", "2026-01", "settled", "2026-02-05", "admin@witapp.it"),
  buildSettlement("st-ph1-202602", "ph1", "2026-02", "open"),
  // ph2
  buildSettlement("st-ph2-202510", "ph2", "2025-10", "settled", "2025-11-06", "admin@witapp.it"),
  buildSettlement("st-ph2-202511", "ph2", "2025-11", "settled", "2025-12-04", "admin@witapp.it"),
  buildSettlement("st-ph2-202512", "ph2", "2025-12", "settled", "2026-01-09", "admin@witapp.it"),
  buildSettlement("st-ph2-202601", "ph2", "2026-01", "settled", "2026-02-06", "admin@witapp.it"),
  buildSettlement("st-ph2-202602", "ph2", "2026-02", "open"),
  // ph3
  buildSettlement("st-ph3-202511", "ph3", "2025-11", "settled", "2025-12-05", "admin@witapp.it"),
  buildSettlement("st-ph3-202512", "ph3", "2025-12", "settled", "2026-01-10", "admin@witapp.it"),
  buildSettlement("st-ph3-202601", "ph3", "2026-01", "settled", "2026-02-07", "admin@witapp.it"),
  buildSettlement("st-ph3-202602", "ph3", "2026-02", "open")
];

export const getSettlementsByPharmacy = (pharmacyId) =>
  settlementsData.filter(s => s.pharmacyId === pharmacyId)
    .sort((a, b) => b.period.localeCompare(a.period));

export const getTransactionsByPharmacy = (pharmacyId) =>
  paymentTransactionsData.filter(t => t.pharmacyId === pharmacyId);

// Helpers UI
export const formatEUR = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const getPatientName = (id) => {
  const p = patientsData.find(x => x.id === id);
  return p ? `${p.firstName} ${p.lastName}` : id;
};

export const getPharmacyName = (id) => {
  const p = pharmaciesData.find(x => x.id === id);
  return p ? p.name : id;
};
